class SwymWishlistButton extends HTMLElement {
  constructor() {
    super();
    this.elements = {};
    this.initializeElements();
  }

  connectedCallback() {
    this.setupWishlistFunctions();
    this.setButtonProcessing(false);
    this.updateButtonState();
  }

  static observedAttributes = ['data-variant-id'];

  initializeElements() {
    const wishlistButton = this.querySelector('#swymcs-wishlist-button');
    if (wishlistButton) {
      this.elements.wishlistButton = wishlistButton;
      
      // Add click event listener
      wishlistButton.addEventListener('click', (e) => {
        e.preventDefault();
        this.handleWishlistClick();
      });

      this.setButtonProcessing(false);
    }
  }

  attributeChangedCallback(name, oldValue, newValue) {
    this.updateButtonState();
  }

  setupWishlistFunctions() {
    // Initialize SwymCallbacks if not already done
    if (!window.SwymCallbacks) {
      window.SwymCallbacks = [];
    }
    
    // Add our init function to SwymCallbacks
    window.swymcsWishlistFunctions = {
      refreshList: this.refreshList.bind(this),
      updateButtonState: this.updateButtonState.bind(this),
      updateButtonWishlistState: this.updateButtonWishlistState.bind(this),
      addToWishlist: this.addToWishlist.bind(this),
      removeFromWishlist: this.removeFromWishlist.bind(this),
      addSwymEventListener: this.addSwymEventListener.bind(this)
    };

    if (!window.swymcsWishlistFunctions.init) {
      window.swymcsWishlistFunctions.init = this.init.bind(this);
      window.swymcsWishlistFunctions.fetchList = this.fetchList.bind(this);
    }

    window.SwymCallbacks.push(window.swymcsWishlistFunctions.init);
  }

  async init(swat) {
    try {
      await this.fetchList(swat);
    } catch (error) {
      console.error("Error fetching wishlist lists", error);
    }

    if (!window.swymSelectedListId) {
      try {
        await this.createList(swat);
      } catch (error) {
        console.error("Error ensuring wishlist list", error);
      }
    }

    this.addSwymEventListener(swat);
  }

  async fetchList(swat) {
    return new Promise((resolve, reject) => {
      const onSuccess = (lists) => {
        window.swymWishLists = lists;
        window.swymSelectedListId = lists && lists[0] && lists[0].lid;

        resolve(lists);
        this.updateButtonState();
      };

      const onError = (error) => {
        reject(error);
      };

      if (!window.swymWishLists) {
        swat.fetchLists({
          callbackFn: onSuccess,
          errorFn: onError
        });
      } else {
        resolve(window.swymWishLists);
      }
    });
  }

  async createList(swat) {
    const listConfig = { "lname": "My Wishlist" };

    return new Promise((resolve, reject) => {
      const onSuccess = ({ lid }) => {
        window.swymSelectedListId = lid;
        resolve(lid);
      };

      const onError = (error) => {
        console.error("Error while creating a List", error);
        reject(error);
      };

      swat.createList(listConfig, onSuccess, onError);
    });
  }

  async refreshList(swat) {
    window.swymWishLists = null;
    await this.fetchList(swat);
  }

  async ensureActiveList(swat) {
    if (window.swymSelectedListId) {
      return window.swymSelectedListId;
    }

    try {
      await this.fetchList(swat);
    } catch (error) {
      console.error("Error refreshing wishlist lists", error);
    }

    if (window.swymSelectedListId) {
      return window.swymSelectedListId;
    }

    return this.createList(swat);
  }

  addToWishlist(swat, product) {
    return new Promise((resolve, reject) => {
      const lid = window.swymSelectedListId;

      if (!lid) {
        const error = new Error("No wishlist list id available while adding to wishlist");
        console.error(error.message, product);
        reject(error);
        return;
      }

      const onSuccess = async function(addedListItem) {
        // Dispatch a custom event for cart update
        const productCount = window.swymWishLists?.[0]?.listcontents?.length ?? 0;
        const cartUpdateEvent = new CustomEvent('wishlist:itemAdded', {
          detail: {
            productCount: productCount
          }
        });
        document.dispatchEvent(cartUpdateEvent);
        // Refresh the list to update button state
        if (window.swymcsWishlistFunctions) {
          await window.swymcsWishlistFunctions.refreshList(swat);
        }
        resolve(addedListItem);
      };

      const onError = function(error) {
        swat.ui?.showErrorNotification?.({ message: "Error Adding Product to Wishlist" });
        reject(error);
      };

      swat.addToList(lid, product, onSuccess, onError);
    });
  }

  removeFromWishlist(swat, product) {
    return new Promise((resolve, reject) => {
      const lid = window.swymSelectedListId;

      if (!lid) {
        const error = new Error("No wishlist list id available while removing from wishlist");
        console.error(error.message, product);
        reject(error);
        return;
      }

      const onSuccess = async function(deletedProduct) {
        // Dispatch a custom event for cart update 
        const productCount = window.swymWishLists?.[0]?.listcontents?.length ?? 0;
        const cartUpdateEvent = new CustomEvent('wishlist:itemRemoved', {
          detail: {
            productCount: productCount
          }
        });
        document.dispatchEvent(cartUpdateEvent);
        // Refresh the list to update button state
        if (window.swymcsWishlistFunctions) {
          await window.swymcsWishlistFunctions.refreshList(swat);
        }
        resolve(deletedProduct);
      };

      const onError = function(error) {
        swat.ui?.showErrorNotification?.({ message: "Error Removing Product from Wishlist" });
        reject(error);
      };

      swat.deleteFromList(lid, product, onSuccess, onError);
    });
  }

  updateButtonState() {
    if (!this.elements.wishlistButton) return;
    
    const { productId, variantId } = this.dataset;
    let isWishlisted = false;

    if (window.swymWishLists) {
      window.swymWishLists.forEach(list => {
        list.listcontents.forEach(item => {
          if (item.empi == productId && item.epi == variantId) {
            isWishlisted = true;
          }
        });
      });
      this.updateButtonWishlistState(isWishlisted);
    }
  }

  updateButtonWishlistState(isWishlisted) {
    if (!this.elements.wishlistButton) return;

    this.setButtonProcessing(false);

    if (isWishlisted) {
      this.elements.wishlistButton.classList.add('swym-added-custom');
    } else {
      this.elements.wishlistButton.classList.remove('swym-added-custom');
    }
  }

  addSwymEventListener(swat) {
    // Listen for Swym events to update button state
    if (window.SwymEventBroker) {
      window.SwymEventBroker.subscribe('swym:addToList', () => {
        this.updateButtonState();
      });
      
      window.SwymEventBroker.subscribe('swym:deleteFromList', () => {
        this.updateButtonState();
      });
    }
  }

  async handleWishlistClick() {
    const button = this.elements.wishlistButton;
    const productId = this.dataset.productId;
    const variantId = this.dataset.variantId;
    const productUrl = this.dataset.productUrl;

    if (!button || !window._swat) {
      this.setButtonProcessing(false);
      return;
    }

    this.setButtonProcessing(true);

    try {
      await this.ensureActiveList(window._swat);

      if (!window.swymSelectedListId) {
        window._swat?.ui?.showErrorNotification?.({ message: "Unable to load wishlist. Please try again." });
        return;
      }

      // Simple product data
      const product = {
        empi: productId,
        epi: variantId,
        du: productUrl
      };

      // Check if product is already in wishlist
      const isWishlisted = button.classList.contains('swym-added-custom');

      if (isWishlisted) {
        await this.removeFromWishlist(window._swat, product);
      } else {
        await this.addToWishlist(window._swat, product);
      }
    } catch (error) {
      console.error("Swym wishlist action failed", error);
      window._swat?.ui?.showErrorNotification?.({ message: "Unable to update wishlist. Please try again." });
    } finally {
      this.setButtonProcessing(false);
    }
  }

  setButtonProcessing(isProcessing) {
    if (!this.elements.wishlistButton) return;

    if (isProcessing) {
      this.elements.wishlistButton.classList.add('disabled');
      this.elements.wishlistButton.setAttribute('disabled', 'disabled');
      this.elements.wishlistButton.disabled = true;
    } else {
      this.elements.wishlistButton.classList.remove('disabled');
      this.elements.wishlistButton.removeAttribute('disabled');
      this.elements.wishlistButton.disabled = false;
    }
  }
}

customElements.define('swym-wishlist-button', SwymWishlistButton);